import { useReducer } from "react";
import { useRecyclingState } from "@shopify/flash-list";
import {
  getOptimizedGridImageUrl,
  isImageUrlEmptyOrUndefined,
} from "../../Utils";

export interface GridItemState {
  showVideoIcon: boolean;
  playVideo: boolean;
  httpErrorIcon: boolean;
  imageUri: string;
  showErrorImage: boolean;
  showImageContainer: boolean;
  isFavorite: boolean;
  isItemChecked: boolean;
  hovered: boolean;
  tooltipIsOpen: boolean;
  threeDotMenuOpen: boolean;
}

export type GridItemAction =
  | { type: "SET_SHOW_VIDEO_ICON"; payload: boolean }
  | { type: "SET_PLAY_VIDEO"; payload: boolean }
  | { type: "SET_HTTP_ERROR_ICON"; payload: boolean }
  | { type: "SET_IMAGE_URI"; payload: string }
  | { type: "SET_SHOW_ERROR_IMAGE"; payload: boolean }
  | { type: "SET_SHOW_IMAGE_CONTAINER"; payload: boolean }
  | { type: "SET_IS_FAVORITE"; payload: boolean }
  | { type: "SET_IS_ITEM_CHECKED"; payload: boolean }
  | { type: "SET_HOVERED"; payload: boolean }
  | { type: "SET_TOOLTIP_OPEN"; payload: boolean }
  | { type: "SET_THREE_DOT_MENU_OPEN"; payload: boolean }
  | { type: "RESET_VIDEO_STATE" }
  | {
      type: "HANDLE_IMAGE_ERROR";
      payload: { hasVideo: boolean; fallbackUri: string };
    }
  | { type: "BATCH_UPDATE"; payload: Partial<GridItemState> };

export function gridItemReducer(
  state: GridItemState,
  action: GridItemAction
): GridItemState {
  switch (action.type) {
    case "SET_SHOW_VIDEO_ICON":
      return { ...state, showVideoIcon: action.payload };

    case "SET_PLAY_VIDEO":
      return { ...state, playVideo: action.payload };

    case "SET_HTTP_ERROR_ICON":
      return { ...state, httpErrorIcon: action.payload };

    case "SET_IMAGE_URI":
      return { ...state, imageUri: action.payload };

    case "SET_SHOW_ERROR_IMAGE":
      return { ...state, showErrorImage: action.payload };

    case "SET_SHOW_IMAGE_CONTAINER":
      return { ...state, showImageContainer: action.payload };

    case "SET_IS_FAVORITE":
      return { ...state, isFavorite: action.payload };

    case "SET_IS_ITEM_CHECKED":
      return { ...state, isItemChecked: action.payload };

    case "SET_HOVERED":
      return { ...state, hovered: action.payload };

    case "SET_TOOLTIP_OPEN":
      return { ...state, tooltipIsOpen: action.payload };

    case "SET_THREE_DOT_MENU_OPEN":
      return { ...state, threeDotMenuOpen: action.payload };

    case "RESET_VIDEO_STATE":
      return {
        ...state,
        playVideo: false,
        showVideoIcon: false,
      };

    case "HANDLE_IMAGE_ERROR":
      return {
        ...state,
        showImageContainer: !action.payload.hasVideo,
        showErrorImage: !action.payload.hasVideo,
        imageUri: action.payload.hasVideo
          ? state.imageUri
          : action.payload.fallbackUri,
      };

    case "BATCH_UPDATE":
      return { ...state, ...action.payload };

    default:
      return state;
  }
}

/**
 * Custom hook to manage SearchResultGridItem state using useReducer
 * Replaces 10+ useState calls with a single reducer for better performance
 *
 * @param item - The grid item data
 * @param ismItemChecked - External checked state
 * @returns Object containing state and dispatch function
 */
/**
 * For items with no image but with video, we show the video icon, not the error fallback.
 * showErrorImage should only be true when: no image AND no video, or when image load failed.
 */
function getInitialShowErrorImage(item: any): boolean {
  const noImage = isImageUrlEmptyOrUndefined(item);
  if (!noImage) return false;
  return !item?.video_url;
}

/**
 * When recycling, showVideoIcon must reset from item so no-image+video items show the icon.
 */
function getInitialShowVideoIcon(item: any): boolean {
  const noImage = isImageUrlEmptyOrUndefined(item);
  const hasVideo = !!(
    item?.video_url !== null && item?.video_url !== undefined
  );
  return !!(noImage && hasVideo);
}

export function useGridItemState(item: any, ismItemChecked: boolean) {
  const recyclingDeps = [item?.id, item?.image_thumb_url, item?.image_url];
  const videoRecyclingDeps = [
    item?.id,
    item?.image_thumb_url,
    item?.image_url,
    item?.video_url,
  ];

  // Use FlashList's useRecyclingState for values that should persist during recycling
  const [showVideoIcon, setShowVideoIcon] = useRecyclingState(
    getInitialShowVideoIcon(item),
    videoRecyclingDeps,
    () => {}
  );

  const [imageUri, setImageUri] = useRecyclingState(
    getOptimizedGridImageUrl(item),
    recyclingDeps,
    () => {}
  );

  const [showErrorImage, setShowErrorImage] = useRecyclingState(
    getInitialShowErrorImage(item),
    recyclingDeps,
    () => {}
  );

  const [showImageContainer, setShowImageContainer] = useRecyclingState(
    !isImageUrlEmptyOrUndefined(item),
    recyclingDeps,
    () => {}
  );

  // Initial state based on item props
  const initialState: GridItemState = {
    showVideoIcon,
    playVideo: false,
    httpErrorIcon: false,
    imageUri,
    showErrorImage,
    showImageContainer,
    isFavorite: item?.is_favorite ?? false,
    isItemChecked: item?.is_selected || ismItemChecked || false,
    hovered: false,
    tooltipIsOpen: false,
    threeDotMenuOpen: false,
  };

  const [state, dispatch] = useReducer(gridItemReducer, initialState);

  return {
    state: {
      ...state,
      showVideoIcon,
      imageUri,
      showErrorImage,
      showImageContainer,
    },
    dispatch,
    // Expose setters for recycling state
    setShowVideoIcon,
    setImageUri,
    setShowErrorImage,
    setShowImageContainer,
  };
}
